import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Permission } from '../types/permissions';

interface UsePermissionsReturn {
  permissions: Permission[];
  hasPermission: (permissionName: string | string[]) => boolean;
  hasAllPermissions: (permissionNames: string[]) => boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePermissions(): UsePermissionsReturn {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/users/permissions/me');
      setPermissions(response.data || []);
    } catch (err: any) {
      console.error('Erro ao buscar permissões:', err);
      setError(err?.response?.data?.message || 'Erro ao buscar permissões');
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('@LuviApp:token');

    if (token) {
      fetchPermissions();
    } else {
      setLoading(false);
      setPermissions([]);
    }
  }, [fetchPermissions]);

  /**
   * Verifica se o usuário tem pelo menos uma das permissões informadas (lógica OR)
   * @param permissionName - Nome da permissão ou array de nomes
   */
  const hasPermission = useCallback((permissionName: string | string[]): boolean => {
    if (permissions.length === 0) return false;

    const permissionsToCheck = Array.isArray(permissionName)
      ? permissionName
      : [permissionName];

    return permissionsToCheck.some(perm =>
      permissions.some(userPerm => userPerm.name === perm)
    );
  }, [permissions]);

  /**
   * Verifica se o usuário tem TODAS as permissões informadas (lógica AND)
   * @param permissionNames - Array de nomes de permissões
   */
  const hasAllPermissions = useCallback((permissionNames: string[]): boolean => {
    if (permissions.length === 0) return false;

    return permissionNames.every(perm =>
      permissions.some(userPerm => userPerm.name === perm)
    );
  }, [permissions]);

  return {
    permissions,
    hasPermission,
    hasAllPermissions,
    loading,
    error,
    refetch: fetchPermissions,
  };
}
